import { formatCurrency, toUnitCharacter } from "@sa-nu/shared-utils/price";
import type { ReactNode } from "react";

import { LEGAL_DOCUMENTS } from "../../../../constants";

import type { FacilityFeeWaiver } from "./_utils/facility-fee-waiver";
import {
  facilityLowerClassNoAdditionalFeeOnStraddling,
  facilityWaivedFee,
  NO_FACILITY_FEE_WAIVER,
} from "./_utils/facility-fee-waiver";
import { DescriptionHeader } from "./description-header";

export type DescriptionForSaunaMessages = {
  heading: ReactNode;
  saunaFeePerNight: (formattedFee: string) => string;
  noAdditionalFee: string;
  legalNoteBefore: string;
  legalLinkText: string;
  legalNoteAfter: string;
  dynamicPricingNote: string;
  unitCharacter: string;
};

const JA_MESSAGES: DescriptionForSaunaMessages = {
  heading: (
    <>
      こちらのキャビンには、
      <br />
      サウナが設置されています
    </>
  ),
  saunaFeePerNight: (formattedFee) =>
    `1泊あたり ${formattedFee}（税込）のサウナ利用料金がかかります。`,
  noAdditionalFee: "追加料金はかかりません。",
  legalNoteBefore: "",
  legalLinkText: "こちらの利用規則",
  legalNoteAfter: "をお読みいただき、ご同意の上でご利用ください。",
  dynamicPricingNote:
    "※ ただし、「お部屋・日程による変動料金」にはサウナ利用料金が含まれています",
  unitCharacter: "円",
};

/**
 * サウナの説明
 */
export function DescriptionForSauna({
  additionalFee,
  hasDynamicPricing = false,
  feeWaiver = NO_FACILITY_FEE_WAIVER,
  messages = JA_MESSAGES,
}: {
  additionalFee: number;
  hasDynamicPricing?: boolean;
  feeWaiver?: FacilityFeeWaiver;
  messages?: DescriptionForSaunaMessages;
}) {
  // 改定日を跨ぐ滞在のみ、課金の表示をした上で、改定日以降は下位クラスで免除になる表示をする
  // 移行期間が終わったら削除する
  const isLowerClassWaivedOnStraddling =
    additionalFee > 0 && feeWaiver.reason === "LowerClass";
  return (
    <div className="mt-[20px] w-full space-y-[20px]">
      <DescriptionHeader iconSrc="/i/icon_sauna.svg" iconAlt="Sauna">
        {messages.heading}
      </DescriptionHeader>
      <div className="text-xs md:text-sm">
        {isLowerClassWaivedOnStraddling
          ? facilityLowerClassNoAdditionalFeeOnStraddling(
              feeWaiver.accommodationClass,
            )
          : additionalFee > 0
            ? messages.saunaFeePerNight(
                formatCurrency(additionalFee, {
                  unitCharacter: toUnitCharacter(messages.unitCharacter),
                }),
              )
            : feeWaiver.reason === "None"
              ? messages.noAdditionalFee
              : facilityWaivedFee(feeWaiver)}
        <br />
        {messages.legalNoteBefore}
        <a
          data-analytics-id="reservation_form_clicked_sauna_terms"
          href={LEGAL_DOCUMENTS.sauna.url}
          target="_blank"
          className="underline"
        >
          {messages.legalLinkText}
        </a>
        {messages.legalNoteAfter}
        {hasDynamicPricing && (
          <>
            <br />
            <span className="text-skogecha-60">
              {messages.dynamicPricingNote}
            </span>
          </>
        )}
      </div>
    </div>
  );
}
